import React, { useMemo, useState } from "react";
import HorizontalBars from "./HorizontalBars";
import { ALL_CATEGORIES, buildServiceRows, durationText, serviceCategories, servicesTable } from "./beautyStatsMetrics";
import { fmtInt, fmtMoney, fmtMoneyCompact, fmtPct0 } from "./statsFormat";

/**
 * Por servicio tab: which services bill the most. One horizontal bar per service (single lime
 * series, value at the tip, tooltip with the amount billed, its share of the salon's total, how
 * many times it was done and its duration), category chips to narrow it down and a "Ver tabla"
 * twin with the same numbers. Only services of attended appointments count (the back prices each
 * line with the price it was booked at). Without data it shows an empty state.
 *
 * @param {{current: object}} props  `current` is a stats response.
 */
export default function BeautyServiciosTab({ current }) {
  const [category, setCategory] = useState(ALL_CATEGORIES);
  const [view, setView] = useState("chart");

  const chips = useMemo(() => serviceCategories(current.services), [current]);
  // The chosen category can vanish after a filter/range change: show everything instead of nothing.
  const activeCategory = chips.some((chip) => chip.value === category) ? category : ALL_CATEGORIES;
  const rows = useMemo(() => buildServiceRows(current.services, activeCategory), [current, activeCategory]);
  const table = useMemo(() => servicesTable(rows), [rows]);

  const barRows = rows.map((row) => ({ key: row.key, label: row.name, value: row.revenue, valueText: fmtMoneyCompact(row.revenue) }));
  const tip = (bar) => {
    const row = rows.find((r) => r.key === bar.key);
    const duration = durationText(row.durationMinutes);
    return {
      title: `${row.name} · ${row.category}`,
      rows: [
        { value: fmtMoney(row.revenue), label: `facturado, ${fmtPct0(row.share)} del total` },
        { value: fmtInt(row.count), label: duration === "—" ? "veces en el período" : `veces en el período · ${duration}` },
      ],
    };
  };

  return (
    <section className="stats-panel" aria-labelledby="stats-services-title">
      <div className="stats-panel__head">
        <div>
          <h2 className="stats-panel__title" id="stats-services-title">
            Servicios que más facturan
          </h2>
          <p className="stats-panel__sub">Precio de los servicios en turnos atendidos.</p>
        </div>
        {chips.length > 1 && (
          <div className="stats-panel__tools">
            <div className="stats-seg stats-seg--wrap" role="group" aria-label="Categoría">
              {chips.map((chip) => (
                <button key={chip.value} type="button" aria-pressed={activeCategory === chip.value} onClick={() => setCategory(chip.value)}>
                  {chip.label}
                </button>
              ))}
            </div>
            <button type="button" className="stats-ghost-btn" aria-pressed={view === "table"} onClick={() => setView((v) => (v === "chart" ? "table" : "chart"))}>
              {view === "chart" ? "Ver tabla" : "Ver gráfico"}
            </button>
          </div>
        )}
      </div>

      {rows.length === 0 ? (
        <p className="stats-footnote">Todavía no hay servicios facturados en este período para mostrar.</p>
      ) : view === "chart" ? (
        <HorizontalBars rows={barRows} ariaLabel="Facturación por servicio. El detalle está en la vista de tabla." tip={tip} />
      ) : (
        <div className="stats-table-wrap">
          <table>
            <thead>
              <tr>
                {table.headers.map((header, index) => (
                  <th key={header} className={index > 1 ? "num" : undefined}>
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((cells, rowIndex) => (
                <tr key={rows[rowIndex].key}>
                  {cells.map((cell, index) => (
                    <td key={index} className={index > 1 ? "num" : undefined}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
